import {Injectable} from '@angular/core';
import {TorchAPIConfiguration} from '../../config/api.configuration';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ManyResponse} from '../../responses';
import {TorchQueryFilter} from './torch-query-filter.model';
import {Burly} from 'kb-burly';
import {map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TorchQueryFiltersService {

  constructor(protected config: TorchAPIConfiguration, private httpClient: HttpClient) {
  }

  /**
   * Get a user's QueryFilters
   *
   * @param offset
   * @param limit
   * @param search
   * @param entity - Optional entity to filter by
   */
  getFilters(offset: number = 0,
             limit: number = 25,
             search: string | null = null,
             entity: string | null = null): Observable<ManyResponse<TorchQueryFilter>> {

    let searchObject = null;

    if (!!search && search.length > 0) {
      if (!!entity) {
        searchObject = JSON.stringify({displayName: {$cont: search}, entity: {$eq: entity}});
      } else {
        searchObject = JSON.stringify({displayName: {$cont: search}});
      }
    } else if (!!entity) {
      searchObject = JSON.stringify({entity: {$eq: entity}});
    }


    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/filters')
      .addQuery('offset', offset)
      .addQuery('limit', limit)
      .addQuery('s', searchObject, false)
      .get;

    return this.httpClient.get<ManyResponse<TorchQueryFilter>>(endpoint);
  }

  /**
   * Get a single filter by the ID
   * @param id - ID of the filter
   */
  getFilter(id: number): Observable<TorchQueryFilter> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/filters')
      .addSegment(`/${id}`)
      .get;

    return this.httpClient.get<TorchQueryFilter>(endpoint);
  }

  /**
   * Delete a filter
   *
   * @param filter
   */
  deleteFilter(filter: TorchQueryFilter) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/filters')
      .addSegment(`/${filter.id}`)
      .get;

    return this.httpClient.delete(endpoint);
  }

  /**
   * Update a filter
   *
   * @param filter
   */
  updateFilter(filter: TorchQueryFilter): Observable<TorchQueryFilter> {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/filters')
      .addSegment(`/${filter.id}`)
      .get;

    return this.httpClient.patch<TorchQueryFilter>(endpoint, filter);
  }

  /**
   * Create a filter
   *
   * @param filter
   */
  createFilter(filter: TorchQueryFilter) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/filters/bulk')
      .get;

    return this.httpClient.post<TorchQueryFilter[]>(endpoint, {bulk: [filter]}).pipe(
      map(resultArray => resultArray[0] || null)
    );
  }

  /**
   * Check if a filter exists by its display name
   *
   * @param displayName
   */
  checkExists(displayName: string) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/filters')
      .addSegment('/checkName')
      .addQuery('displayName', displayName)
      .get;

    return this.httpClient.get<boolean>(endpoint);
  }

  /**
   * Check if a filter is valid
   *
   * @param filter
   */
  validateFilter(filter: TorchQueryFilter): boolean {
    const validateProperties = ['filterValue', 'displayName', 'entity'];
    let isValid = true;

    validateProperties.forEach(property => {
      if (!filter.hasOwnProperty(property) || !!!filter[property]) {
        isValid = false;
      }
    });

    return isValid;
  }
}
